import React, { useEffect, useState, useRef } from 'react';
import { Send, Sparkles, Brain, ShieldAlert, Truck, Users } from 'lucide-react';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { ChatMessage, Message } from './ui/ChatMessage';
import { streamOpenAIResponse } from '../utils/openai';
import { MOCK_ALERTS, MOCK_VEHICLES, MOCK_GUARDS } from '../utils/mockData';
import { cn } from '../utils/cn';
const QUICK_ACTIONS = [{
  label: 'Analyze Alerts',
  icon: ShieldAlert,
  prompt: 'Analyze current alerts and recommend priority actions.'
}, {
  label: 'Vehicle Summary',
  icon: Truck,
  prompt: 'Summarize vehicle status, focusing on warnings and overdue entries.'
}, {
  label: 'Guard Status',
  icon: Users,
  prompt: 'Report on guard roster status and any coverage gaps.'
}];
// Sample conversation data
const SAMPLE_MESSAGES: Message[] = [{
  id: 'sample-1',
  role: 'assistant',
  content: 'Security Intelligence Online. Monitoring active alerts, vehicle traffic, and guard roster. How can I assist with operations?',
  timestamp: new Date(Date.now() - 300000)
}, {
  id: 'sample-2',
  role: 'user',
  content: 'What are the current high-priority alerts?',
  timestamp: new Date(Date.now() - 240000)
}, {
  id: 'sample-3',
  role: 'assistant',
  content: '**HIGH PRIORITY ALERTS:**\n\n• **[CRITICAL]** Unauthorized Access - Loading Bay 3\n  - Perimeter breach detected\n  - Immediate response required\n\n• **[HIGH]** Equipment Malfunction - Gate Scanner\n  - Vehicle verification system offline\n  - Manual checks in effect\n\n**RECOMMENDATION:** Dispatch security to Loading Bay 3 immediately. Coordinate with IT for scanner repair.',
  timestamp: new Date(Date.now() - 180000)
}, {
  id: 'sample-4',
  role: 'user',
  content: 'Any vehicles with warnings currently on site?',
  timestamp: new Date(Date.now() - 120000)
}, {
  id: 'sample-5',
  role: 'assistant',
  content: '**VEHICLES WITH WARNINGS:**\n\n• **ABC-1234** (Delivery Truck) - Gate A\n  ⚠️ Overdue departure by 45 minutes\n\n• **XYZ-9876** (Cargo Van) - Gate B\n  ⚠️ Expired facility clearance\n\n**ACTION:** Contact drivers for immediate resolution. Consider escort for XYZ-9876 pending clearance renewal.',
  timestamp: new Date(Date.now() - 60000)
}];
export function AIAssistant() {
  const [messages, setMessages] = useState<Message[]>(SAMPLE_MESSAGES);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  const generateSystemContext = () => {
    // Check if mock data is available
    if (!MOCK_ALERTS || !MOCK_VEHICLES || !MOCK_GUARDS) {
      return `You are a specialized Security Operations AI Assistant for a logistics facility.
      
NOTE: This is a sample demonstration. Real-time facility data is not currently available.

Please provide general security operations guidance and sample responses based on typical logistics facility scenarios.`;
    }
    const alerts = MOCK_ALERTS.map(a => `- [${a.severity.toUpperCase()}] ${a.type} in ${a.zone} (${a.details})`).join('\n');
    const vehicles = MOCK_VEHICLES.filter(v => v.status === 'Inside').map(v => `- ${v.plate} (${v.type}) at ${v.gate}. Warning: ${v.hasWarning ? v.warningReason : 'None'}`).join('\n');
    const guards = MOCK_GUARDS.map(g => `- ${g.name}: ${g.status}`).join('\n');
    return `You are a specialized Security Operations AI Assistant for a logistics facility.
    
CURRENT FACILITY STATUS:

ACTIVE ALERTS:
${alerts}

VEHICLES ON SITE:
${vehicles}

GUARD ROSTER:
${guards}

INSTRUCTIONS:
- Provide concise, tactical responses suitable for security professionals.
- Prioritize high-severity alerts and safety risks.
- Use bullet points for clarity.
- Maintain a professional, authoritative tone.
- Do not hallucinate data not provided above.`;
  };
  const handleSend = async (content: string) => {
    if (!content.trim() || isLoading) return;
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    // Create placeholder for AI response
    const aiMessageId = (Date.now() + 1).toString();
    const aiMessage: Message = {
      id: aiMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isStreaming: true
    };
    setMessages(prev => [...prev, aiMessage]);
    const apiMessages = [{
      role: 'system' as const,
      content: generateSystemContext()
    }, ...messages.slice(-4).map(m => ({
      role: m.role,
      content: m.content
    })), {
      role: 'user' as const,
      content
    }];
    await streamOpenAIResponse({
      messages: apiMessages,
      onToken: token => {
        setMessages(prev => prev.map(m => m.id === aiMessageId ? {
          ...m,
          content: m.content + token
        } : m));
      },
      onComplete: () => {
        setMessages(prev => prev.map(m => m.id === aiMessageId ? {
          ...m,
          isStreaming: false
        } : m));
        setIsLoading(false);
      },
      onError: error => {
        console.error(error);
        setMessages(prev => prev.map(m => m.id === aiMessageId ? {
          ...m,
          content: '**[SAMPLE OUTPUT]** Connection to live AI unavailable. This is demonstration data showing typical security intelligence responses. In production, real-time analysis would be provided here.',
          isStreaming: false
        } : m));
        setIsLoading(false);
      }
    });
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(input);
    }
  };
  return <Card className="w-full flex flex-col bg-[#1a1d23] border-gray-800 overflow-hidden" noPadding data-id="element-4">
      {/* Horizontal Layout Container */}
      <div className="flex items-stretch h-[280px]" data-id="element-5">
        {/* Left: Header & Quick Actions */}
        <div className="w-64 border-r border-gray-800 flex flex-col bg-[#15171c]" data-id="element-6">
          <div className="p-4 border-b border-gray-800" data-id="element-7">
            <div className="flex items-center gap-2" data-id="element-8">
              <div className="relative" data-id="element-9">
                <div className="absolute inset-0 bg-amber-500/20 rounded-full animate-pulse" data-id="element-10"></div>
                <Brain className="h-5 w-5 text-amber-500 relative z-10" data-id="element-11" />
              </div>
              <div data-id="element-12">
                <h2 className="text-sm font-semibold text-white tracking-wide" data-id="element-13">
                  SECURITY INTEL
                </h2>
                <div className="flex items-center gap-1.5" data-id="element-14">
                  <span className="h-1.5 w-1.5 rounded-full bg-green-500" data-id="element-15"></span>
                  <span className="text-[10px] text-gray-400 uppercase tracking-wider" data-id="element-16">
                    Sample Data
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex-1 p-3 space-y-2 overflow-y-auto" data-id="element-17">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2" data-id="element-18">
              Quick Actions
            </p>
            {QUICK_ACTIONS.map(action => <button key={action.label} onClick={() => handleSend(action.prompt)} disabled={isLoading} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-800/50 border border-gray-700 text-xs text-gray-300 hover:bg-gray-800 hover:text-amber-500 hover:border-amber-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-19">
                <action.icon className="h-3.5 w-3.5 flex-shrink-0" data-id="element-20" />
                <span className="text-left" data-id="element-21">{action.label}</span>
              </button>)}
          </div>

          {isLoading && <div className="p-3 border-t border-gray-800 flex items-center justify-center gap-1" data-id="element-22">
              <span className="h-1 w-1 bg-amber-500 rounded-full animate-bounce [animation-delay:-0.3s]" data-id="element-23"></span>
              <span className="h-1 w-1 bg-amber-500 rounded-full animate-bounce [animation-delay:-0.15s]" data-id="element-24"></span>
              <span className="h-1 w-1 bg-amber-500 rounded-full animate-bounce" data-id="element-25"></span>
            </div>}
        </div>

        {/* Right: Messages & Input */}
        <div className="flex-1 flex flex-col" data-id="element-26">
          {/* Messages Area - Horizontal Scroll */}
          <div className="flex-1 overflow-x-auto overflow-y-hidden p-4" data-id="element-27">
            <div className="flex gap-4 h-full items-start min-w-max" data-id="element-28">
              {messages.map(msg => <div key={msg.id} className="w-80 flex-shrink-0" data-id="element-29">
                  <ChatMessage message={msg} data-id="element-30" />
                </div>)}
              <div ref={messagesEndRef} data-id="element-31" />
            </div>
          </div>

          {/* Input Area */}
          <div className="p-4 border-t border-gray-800 bg-[#15171c]" data-id="element-32">
            <div className="relative flex items-center gap-2" data-id="element-33">
              <input ref={inputRef} type="text" value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown} placeholder="Ask about alerts, vehicles, or security status..." disabled={isLoading} className="flex-1 bg-gray-900/50 border border-gray-700 rounded-lg px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/50 transition-all disabled:opacity-50" data-id="element-34" />
              <Button size="icon" variant="primary" onClick={() => handleSend(input)} disabled={!input.trim() || isLoading} className={cn('h-10 w-10 shrink-0 transition-all', isLoading ? 'bg-gray-700 text-gray-400' : 'bg-amber-500 text-black hover:bg-amber-400')} data-id="element-35">
                <Send className="h-4 w-4" data-id="element-36" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Card>;
}